import React, {
	useMemo,
	useCallback,
	forwardRef,
	type ReactNode,
	type SyntheticEvent,
} from 'react';
import noop from 'lodash/noop';
import { Box, Pressable } from '@atlaskit/primitives';
import { asyncEditIssueTypeModalEntrypoint } from '@atlassian/jira-edit-issue-type-modal/entrypoint.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import type { RuntimePropsOfEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import type { ConfigureSource } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
import type { UFOExperience } from '@atlassian/ufo';

export type EditIssueTypeModalTriggerProps = {
	onIssueTypeUpdated?: () => void;
	onOpen: () => void;
	onClose: () => void;
	relevantIssueTypeIds?: string[];
	issueTypeId: string;
	projectId: number;
	projectKey: string;
	calledFrom: ConfigureSource;
	ufoExperience: UFOExperience;
	children: ReactNode;
	isPressable?: boolean;
};
type RuntimeProps = RuntimePropsOfEntryPoint<typeof asyncEditIssueTypeModalEntrypoint>;

export const EditIssueTypeModalTrigger = forwardRef<HTMLElement, EditIssueTypeModalTriggerProps>(
	(
		{
			onIssueTypeUpdated = noop,
			onOpen,
			onClose,
			relevantIssueTypeIds,
			calledFrom,
			issueTypeId,
			projectId,
			projectKey,
			ufoExperience,
			children,
			isPressable = false,
		}: EditIssueTypeModalTriggerProps,
		forwardedRef,
	) => {
		const cloudId = useCloudId();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const entryPointParams = useMemo(
			() => ({
				projectKey,
				cloudId,
			}),
			[projectKey, cloudId],
		);

		const onModalClose = useCallback(() => {
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'inlineEditIssueTypeModal',
					action: 'closed',
				}),
			);
			onClose();
		}, [createAnalyticsEvent, onClose]);

		const entryPointProps: RuntimeProps = useMemo(
			() => ({
				onRefetchIssueTypesWhenIssueTypeUpdated: onIssueTypeUpdated,
				onClose: onModalClose,
				onOpen,
				calledFrom,
				ufoExperience,
				shouldShowIssueTypeSelect: true,
				issueTypeId,
				projectId,
				projectKey,
				relevantIssueTypeIds,
			}),
			[
				onIssueTypeUpdated,
				onModalClose,
				onOpen,
				calledFrom,
				ufoExperience,
				issueTypeId,
				projectId,
				projectKey,
				relevantIssueTypeIds,
			],
		);

		const onClick = useCallback(
			(e: SyntheticEvent) => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
					}),
					'editIssueTypeButton',
					{ source: calledFrom },
				);

				// If we triggered the click manually for keyboard users (isPressable = false)
				// this handler becomes the source-of-truth handler for both keyboard users and mouse users
				// However, we need to stopPropagation from here to prevent double modal for mouse users
				// When mouse user clicks the button, if we don't stop propagation here, option change handler
				// will be called which will trigger click manually again and cause double modal rendering
				!isPressable && e.stopPropagation();
			},
			[calledFrom, createAnalyticsEvent, isPressable],
		);

		const Wrapper = isPressable ? Pressable : Box;

		return (
			<ModalEntryPointPressableTrigger
				entryPoint={asyncEditIssueTypeModalEntrypoint}
				entryPointParams={entryPointParams}
				entryPointProps={entryPointProps}
				interactionName="edit-issue-type-modal"
				useInternalModal
				modalProps={defaultModalProps}
				forwardedRef={forwardedRef}
			>
				{({ ref }) => (
					// @ts-expect-error - The reference type from ModalEntryPointPressableTrigger is incompatible with Pressable/Box,
					// but this works correctly at runtime and is a type-system limitation
					<Wrapper testId="edit-issue-type-modal-trigger.wrapper" onClick={onClick} ref={ref}>
						{children}
					</Wrapper>
				)}
			</ModalEntryPointPressableTrigger>
		);
	},
);
